import * as React from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Select, MenuItem } from '@mui/material';
import { useState, useEffect } from 'react';
import { ComponentSpinner } from '../../Compounds/Loading/ComponentSpinner';
import { TextBlock } from '../../Atoms/Text';
import { createNewPaymentRecipient, PaymentRecipient, PaymentRecipientType } from '../../../store/PaymentRecipient';

interface NewRecipientDialogProps {
  open: boolean;
  onClose: () => void;
}

export const NewRecipientDialog = ({ open, onClose }: NewRecipientDialogProps) => {
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState<string>('');
  const [type, setType] = useState<PaymentRecipientType | ''>('');

  useEffect(() => {
    if (open) {
      setName('');
      setType('');
    }
  }, [open]);

  const handleSave = async () => {
    if (!name || !type) return;
    setLoading(true);
    const newRecipient = {
      name,
      type,
      active: true
    } as unknown as PaymentRecipient;
    await createNewPaymentRecipient(newRecipient);
    setLoading(false);
    onClose();
  };

  const handleClose = () => {
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      slotProps={{
        paper: {
          component: 'form',
          onSubmit: (event: React.SubmitEvent) => {
            event.preventDefault();
            handleSave();
          },
          sx: { overflowY: 'visible' }
        }
      }}
      fullWidth
      maxWidth="sm"
      data-testid="create-recipient-modal">
      <DialogTitle>
        <TextBlock
          weight="bold"
          size="lg"
          color="grey9">
          New Recipient
        </TextBlock>
      </DialogTitle>
      <DialogContent sx={{ minHeight: 70, overflowY: 'visible', paddingLeft: 3, paddingRight: 3 }}>
        {loading && <ComponentSpinner label="Saving..." />}
        {!loading && (
          <div className="flex flex-row gap-x-10 w-full">
            <div className="flex flex-col gap-y-3 w-1/2">
              <TextBlock color="grey6">Name</TextBlock>
              <TextField
                sx={{ width: '100%' }}
                value={name}
                onChange={(e) => setName(e?.target?.value)}
                onBlur={() => setName((prev) => prev.trim())}
                size="small"
              />
            </div>
            <div className="flex flex-col gap-y-3 w-1/2">
              <TextBlock color="grey6">Type</TextBlock>
              <Select
                value={type}
                onChange={(e) => setType(e?.target?.value)}
                size="small"
                sx={{ width: '100%' }}>
                {Object.values(PaymentRecipientType).map((typeOption) => (
                  <MenuItem
                    key={typeOption}
                    value={typeOption}>
                    {typeOption}
                  </MenuItem>
                ))}
              </Select>
            </div>
          </div>
        )}
      </DialogContent>
      <hr className="mb-1 mx-6" />
      {!loading && (
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            data-testid="cancel-button"
            onClick={handleClose}>
            Cancel
          </Button>
          <Button
            disabled={!name || !type}
            type="submit"
            variant="contained">
            Save
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};
